import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'

import {
  createPicker,
  DEFAULT_GROUP_ID,
  selectGroupDisabled,
  selectGroupLevel1Label,
  selectGroupSubtitle,
  selectGroupTitle,
  selectHasAnySelection,
  selectPickerIdsForGroup,
} from 'slices/aiSegmentBuilder/picker'

import BottomSelectorContainer from './BottomSelector.container'
import TopOperatorContainer from './TopOperator.container'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface SingleLevelPickerGroupContainerProps {
  /** Unique identifier for this picker group */
  groupId?: string
  /** Whether the group has been hydrated from server data (skips auto-init) */
  hasHydrated?: boolean
  /** Callback when the group is confirmed */
  onConfirm?: (groupId: string) => void
  /** Custom styles for the container */
  style?: React.CSSProperties
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Container for a single-level picker group (e.g., locations).
 * Displays a title, subtitle with operator, one multiselect dropdown,
 * and a confirm button. No cards, no top selector, no "Add" button.
 */
const SingleLevelPickerGroupContainer = React.memo(
  ({
    groupId = DEFAULT_GROUP_ID,
    hasHydrated = false,
    onConfirm,
    style,
  }: SingleLevelPickerGroupContainerProps) => {
    const dispatch = useDispatch()
    const [css, theme] = useStyletron()

    const pickerIds = useSelector((state: any) =>
      selectPickerIdsForGroup(state, groupId)
    )
    const disabled = useSelector((state: any) =>
      selectGroupDisabled(state, groupId)
    )
    const title = useSelector((state: any) => selectGroupTitle(state, groupId))
    const subtitle = useSelector((state: any) =>
      selectGroupSubtitle(state, groupId)
    )
    const level1Label = useSelector((state: any) =>
      selectGroupLevel1Label(state, groupId)
    )
    const hasAnySelection = useSelector((state: any) =>
      selectHasAnySelection(state, groupId)
    )

    const level1LabelLower = (level1Label || 'item').toLowerCase()
    const placeholder = `Select ${level1LabelLower}...`

    // Initialize picker on mount (only if not server-driven)
    useEffect(() => {
      if (!hasHydrated && pickerIds.length === 0) {
        dispatch(createPicker({ groupId }))
      }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const handleConfirm = useCallback(() => {
      onConfirm?.(groupId)
    }, [onConfirm, groupId])

    // ═════════════════════════════════════════════════════════════════════════
    // Styles
    // ═════════════════════════════════════════════════════════════════════════

    const containerClassName = css({
      marginBottom: '24px',
      padding: '16px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: theme.colors.grey30,
      borderRadius: '8px',
      backgroundColor: theme.colors.grey10,
    })

    const subtitleClassName = css({
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginTop: '4px',
    })

    const pickersListClassName = css({
      marginBottom: '16px',
    })

    const actionsClassName = css({
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    })

    // Parse subtitle template - replace {operator} with TopOperator component
    const renderSubtitle = () => {
      if (!subtitle) {
        return (
          <>
            Must include{' '}
            <TopOperatorContainer groupId={groupId} disabled={disabled} /> of
            the following {level1LabelLower}(s)
          </>
        )
      }
      const parts = subtitle.split('{operator}')
      if (parts.length === 1) {
        return <>{subtitle}</>
      }
      return (
        <>
          {parts[0]}
          <TopOperatorContainer groupId={groupId} disabled={disabled} />
          {parts[1]}
        </>
      )
    }

    // Single-level uses one picker with a bottom multiselect dropdown
    const pickerId = pickerIds[0]

    return (
      <div className={containerClassName} style={style}>
        {/* Header */}
        <div className="mb-m">
          <Text.Header4 tag="h3" color="grey90">
            {title}
          </Text.Header4>
          <Text.BodyText4 tag="p" color="grey70" className={subtitleClassName}>
            {renderSubtitle()}
          </Text.BodyText4>
        </div>

        {/* Multiselect dropdown */}
        <div className={pickersListClassName}>
          {pickerId !== undefined && (
            <BottomSelectorContainer
              pickerId={pickerId}
              disabled={disabled}
              placeholder={placeholder}
            />
          )}
        </div>

        {/* Actions */}
        <div className={actionsClassName}>
          <Button
            kind="primary"
            onClick={handleConfirm}
            disabled={disabled || !hasAnySelection}
          >
            Confirm
          </Button>
        </div>
      </div>
    )
  }
)

SingleLevelPickerGroupContainer.displayName = 'SingleLevelPickerGroupContainer'

export default SingleLevelPickerGroupContainer
